import React from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';
import findIndex from 'lodash/findIndex';
import InnerHTML from '@Components/InnerHTML';
import * as WindowHelper from '@Services/WindowHelper';

export default class SynonymsComponent extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isExpanded: false,
      more: 0,
    };

    // Tricky coefficient for calculation of container width in characters, ¯\_(ツ)_/¯
    const { coefficient } = this.props;
    this.coefficient = coefficient || 0.425;

    this._recalculateMore = this._recalculateMore.bind(this);
    this.containerRef = React.createRef();
    this.onResizeUnsubscribe = null;
  }

  componentDidMount() {
    this._recalculateMore();

    this.onResizeUnsubscribe = WindowHelper.onResize(throttle(this._recalculateMore, 300));
  }

  componentWillUnmount() {
    if (this.onResizeUnsubscribe) this.onResizeUnsubscribe();
  }

  setExpanded(isExpanded) {
    this.setState({ isExpanded });
  }

  getSynonymsHtmlStr() {
    const { list, prefix, separator } = this.props;
    const { isExpanded, more } = this.state;

    const result = isExpanded ? list : list.slice(0, list.length - more);
    let htmlStr = `${prefix}${result.join(separator)}`;

    if (!isExpanded && more > 0) {
      htmlStr += separator;
    }

    return htmlStr;
  }

  getSynonymsList() {
    const { list, prefix } = this.props;
    const { isExpanded, more } = this.state;

    const result = isExpanded ? list : list.slice(0, list.length - more);
    const resultArray = result.map((item, index) => {
      return (
        <React.Fragment key={item}>
          <InnerHTML tag="span" html={item} />
          {(index !== result.length - 1 || (!isExpanded && more > 0)) && <span className="separator" />}
        </React.Fragment>
      );
    });

    return (
      <div>
        <span>{prefix}</span>
        {resultArray}
        {this.getLessMoreLabel()}
      </div>
    );
  }

  getLessMoreLabel = () => {
    const { isExpanded, more } = this.state;
    const { showCount } = this.props;
    // eslint-disable-next-line no-nested-ternary
    return isExpanded ? (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
      <span role="button" tabIndex="0" className="show-less-link" onClick={() => this.setExpanded(false)}>
        {' '}
        - show less
      </span>
    ) : more > 0 ? (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
      <span role="button" tabIndex="0" className="angular-ellipsis-append" onClick={() => this.setExpanded(true)}>
        {' '}
        +{showCount && more} more
      </span>
    ) : null;
  };

  _recalculateMore() {
    const { prefix, list, separator } = this.props;
    const el = this.containerRef.current;

    const fontSize = parseFloat(window.getComputedStyle(el, null).getPropertyValue('font-size'));
    const widthInCh = el.offsetWidth / (fontSize * this.coefficient);
    // 2 lines of text - prefix
    const maxAllowedLenForSynonyms = widthInCh * 2 - prefix.length;

    // find first element that will not fit into 2 lines
    let tmpStr = '';
    const idx = findIndex(list, (synonym) => {
      tmpStr += synonym + separator;
      return tmpStr.length >= maxAllowedLenForSynonyms;
    });

    let more;
    if (idx === 0) {
      more = list.length - 1;
    } else if (idx !== -1) {
      more = list.length - idx;
    } else {
      more = 0;
    }

    this.setState({ more });
  }

  render() {
    const { isDotSeparated } = this.props;

    return (
      <div className="rx-element-literature" ref={this.containerRef}>
        {!isDotSeparated && (
          <>
            <InnerHTML tag="span" html={this.getSynonymsHtmlStr()} />
            {this.getLessMoreLabel()}
          </>
        )}
        {isDotSeparated && this.getSynonymsList()}
      </div>
    );
  }
}

SynonymsComponent.propTypes = {
  list: PropTypes.array.isRequired,
  prefix: PropTypes.string.isRequired,
  showCount: PropTypes.bool.isRequired,
  separator: PropTypes.string,
  coefficient: PropTypes.number,
  isDotSeparated: PropTypes.bool,
};
